/**
 * How long ago something happened, in the notification centre's wording.
 *
 * This replaces date-fns' `formatDistanceToNow`, the only thing the library
 * was used for and 46KB of the main bundle to provide it. The output is
 * deliberately identical, thresholds included — tests/relativeTime.test.ts
 * holds expectations taken from date-fns itself, across every bucket boundary,
 * so this is a like-for-like swap and not a rewording of every timestamp in
 * the app.
 *
 * The caller already stripped date-fns' "about " prefix, so it is simply not
 * produced here.
 */

const MINUTES_IN_DAY = 1440;
/** date-fns' own constant: a "month" is 30 days for the purpose of rounding. */
const MINUTES_IN_MONTH = 43200;

/**
 * Whole calendar months between two dates, as date-fns' differenceInMonths
 * computes it — the day of the month has to have come round again for the
 * month to count.
 */
function calendarMonthsBetween(earlier: Date, later: Date): number {
  let months =
    (later.getFullYear() - earlier.getFullYear()) * 12 +
    (later.getMonth() - earlier.getMonth());
  const anchor = new Date(earlier.getTime());
  anchor.setMonth(anchor.getMonth() + months);
  if (anchor.getTime() > later.getTime()) months -= 1;
  return months;
}

const unit = (n: number, name: string) => `${n} ${name}${n === 1 ? '' : 's'}`;

export function distanceToNow(from: Date | string | number, now: Date = new Date()): string {
  const then = from instanceof Date ? from : new Date(from);
  const ms = now.getTime() - then.getTime();
  if (!Number.isFinite(ms)) return '';

  const [earlier, later] = ms >= 0 ? [then, now] : [now, then];
  const seconds = Math.trunc(Math.abs(ms) / 1000);
  // Rounded to a whole minute BEFORE any threshold is tested, exactly as
  // date-fns does. It matters: 44.5 minutes rounds to 45 and so reads as an
  // hour, not as "45 minutes".
  const minutes = Math.round(seconds / 60);

  // includeSeconds: true — the sub-minute buckets, which exist so a
  // just-arrived notification does not sit on "less than a minute" for a
  // whole minute.
  if (minutes < 2) {
    if (seconds < 5) return 'less than 5 seconds';
    if (seconds < 10) return 'less than 10 seconds';
    if (seconds < 20) return 'less than 20 seconds';
    if (seconds < 40) return 'half a minute';
    if (seconds < 60) return 'less than a minute';
    return '1 minute';
  }
  if (minutes < 45) return unit(minutes, 'minute');
  // 45 minutes up to 90 minutes: date-fns says "about 1 hour".
  if (minutes < 90) return '1 hour';
  if (minutes < MINUTES_IN_DAY) return unit(Math.round(minutes / 60), 'hour');
  // 1 day up to 1.75 days.
  if (minutes < 2520) return '1 day';
  if (minutes < MINUTES_IN_MONTH) return unit(Math.round(minutes / MINUTES_IN_DAY), 'day');
  // 1 month up to 2 months.
  if (minutes < MINUTES_IN_MONTH * 2) return unit(Math.round(minutes / MINUTES_IN_MONTH), 'month');

  // Past two months date-fns switches to calendar months for the BRANCH while
  // still rounding the printed number against a 30-day month. The two disagree
  // either side of a long month, which is why the branch cannot just be
  // derived from the rounded value.
  const months = calendarMonthsBetween(earlier, later);
  if (months < 12) return unit(Math.round(minutes / MINUTES_IN_MONTH), 'month');

  const monthsSinceStartOfYear = months % 12;
  const years = Math.trunc(months / 12);
  if (monthsSinceStartOfYear < 3) return unit(years, 'year');   // "about N years"
  if (monthsSinceStartOfYear < 9) return `over ${unit(years, 'year')}`;
  return `almost ${unit(years + 1, 'year')}`;
}
